import "./meshKernelsBootstrap";
import { getActivePage, type CurvaDocument } from "@/document/model/document";
import { getEffectiveNodeStyle, getEffectiveTextModel } from "@/document/model/documentStyles";
import { flexChildLayout, frameCornerRadii, type FrameNode, type SceneNode } from "@/document/model/nodes";
import { parseColor, type OklchColor } from "@/document/model/color";
import { normalizeFillStack, normalizeStrokeStack, type Paint } from "@/document/model/styles";
import type { RichTextModel, TypeStyle } from "@/document/model/text";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import { frameUsesPatternLayout, patternInstancesForFrame } from "@/document/operations/patternLayout";
import { arcOvalTriangles, ovalOutlinePoints, primitiveToPolygon, primitiveToTriangles } from "@/document/operations/primitiveGeometry";
import { sceneNodeToContours, signedArea, pathDataToContours } from "@/document/operations/pathGeometry";
import type { Rect } from "@/lib/math/rect";
import { applyAffinePoint, IDENTITY_AFFINE, multiplyAffine, type Affine2D } from "@/lib/math/affine";
import type { DisplayItem, DisplayList, DisplayPaint } from "../displayList";
import { applyDisplayWorldTransform, composeDisplayWorldTransform } from "../displayTransform";
import {
  booleanPreviewNode,
  boundsFromClipPoints,
  boundsFromPoints,
  cachedPathMesh,
  cachedStrokeDisplayItems,
  clipFromRect,
  clipFromRoundedRect,
  fillDisplayItems,
  intersectClips,
  normalizedCornerRadii,
  paintToDisplayPaint,
  rectPoints,
  rectVertices,
  roundedRectClipPolygon,
} from "./pathNodes";
import { networkToDisplayItems, previewNetworkToDisplayItems } from "./vectorNodes";
import { ellipsePoints, ellipseSegmentCount, strokeDisplayItems } from "./strokeMesh";
import { packMesh } from "./meshKernels";

export interface DisplayListOptions {
  activePageId?: string;
  vectorNetworkPreviews?: Map<string, { network: VectorNetwork; bounds: Rect; changedVertexIds?: string[]; changedEdgeIds?: string[] }>;
  booleanSourcePreviews?: Map<string, Rect>;
  textPreviews?: Map<string, RichTextModel>;
  hiddenNodeIds?: Set<string>;
  /** When set with reuseItems, leaf nodes not in this set reuse prior display items. */
  dirtyNodeIds?: Set<string>;
  reuseItems?: Map<string, DisplayItem[]>;
}

/** Full tessellation walk without retained-scene adoption. */
export function documentToDisplayListUncached(document: CurvaDocument, options: DisplayListOptions = {}): DisplayList {
  const activePage = getActivePage(document, options.activePageId);
  const pageChildren = activePage?.children ?? [];
  return {
    items: pageChildren.flatMap(node => nodeToDisplayItems(document, node, undefined, options)),
  };
}

export function sceneNodesToDisplayItems(document: CurvaDocument, nodes: SceneNode[], activeClip?: DisplayItem["clip"]): DisplayItem[] {
  return nodes.flatMap(node => nodeToDisplayItems(document, node, activeClip));
}

/**
 * Tessellate a single node subtree under a known parent world transform.
 * Used by the resize preview so a re-tessellated node lands in the same world
 * space (including any rotated ancestors) as the committed scene.
 */
export function sceneNodeToDisplayItemsWithParent(
  document: CurvaDocument,
  node: SceneNode,
  activeClip: DisplayItem["clip"] | undefined,
  parentWorldTransform: Affine2D,
): DisplayItem[] {
  return nodeToDisplayItems(document, node, activeClip, {}, 1, parentWorldTransform);
}

function resolveNodeForDisplay(document: CurvaDocument, node: SceneNode, options: DisplayListOptions = {}): SceneNode {
  let resolved = node;
  if (node.styleBindings && Object.keys(node.styleBindings).length > 0) {
    const style = getEffectiveNodeStyle(document, node);
    if (node.type === "text") {
      const text = getEffectiveTextModel(document, node) ?? node.text;
      resolved = { ...node, style, text };
    } else {
      resolved = { ...node, style };
    }
  }

  if (resolved.type === "text") {
    const previewText = options.textPreviews?.get(resolved.id);
    if (previewText) {
      resolved = { ...resolved, text: previewText };
    }
  }

  return resolved;
}

function nodeToDisplayItems(
  document: CurvaDocument,
  node: SceneNode,
  activeClip?: DisplayItem["clip"],
  options: DisplayListOptions = {},
  inheritedOpacity = 1,
  parentWorldTransform: Affine2D = IDENTITY_AFFINE,
  dirtyAncestor = false,
): DisplayItem[] {
  if (options.hiddenNodeIds?.has(node.id)) {
    return [];
  }

  node = resolveNodeForDisplay(document, node, options);
  const opacity = clamp01(inheritedOpacity * (Number.isFinite(node.style.opacity) ? node.style.opacity : 1));
  if (!node.visible || opacity <= 0) {
    return [];
  }
  if (node.isMask) {
    return [];
  }

  const worldTransform = composeDisplayWorldTransform(parentWorldTransform, node);
  const nodeDirty = Boolean(options.dirtyNodeIds?.has(node.id) || dirtyAncestor);
  const canReuse = Boolean(
    options.reuseItems
    && options.dirtyNodeIds
    && !nodeDirty
    && options.reuseItems.has(node.id)
    && node.type !== "frame"
    && node.type !== "group"
    && node.type !== "component"
    && node.type !== "componentVariant"
    && node.type !== "instance",
  );
  if (canReuse) {
    // Items are already finalized in world space from the prior retained build.
    return options.reuseItems!.get(node.id)!.map(item => ({
      ...item,
      clip: activeClip,
    }));
  }

  const childDirtyAncestor = dirtyAncestor || Boolean(options.dirtyNodeIds?.has(node.id));

  if (node.type === "frame") {
    const normalizedRadii = normalizedCornerRadii(node.bounds, frameCornerRadii(node));
    const hasRoundedCorners = normalizedRadii.some(radius => radius > 0);
    const frameOutline = hasRoundedCorners ? roundedRectClipPolygon(node.bounds, normalizedRadii) : rectPoints(node.bounds);
    const items = fillDisplayItems(
      node,
      hasRoundedCorners ? "roundedRect" : "triangles",
      node.bounds,
      packMesh(rectVertices(node.bounds)),
      activeClip,
      "#f8fafc",
      hasRoundedCorners ? normalizedRadii : undefined,
    );
    const frameItems = [...items, ...strokeDisplayItems(node, frameOutline, true).map(item => ({ ...item, clip: activeClip }))];
    const childClip = node.clipsContent
      ? intersectClips(
          activeClip,
          hasRoundedCorners
            ? clipFromRoundedRect(node.bounds, worldTransform, normalizedRadii)
            : clipFromRect(node.bounds, worldTransform),
        )
      : activeClip;
    return [
      ...withNodeEffects(node, frameItems, opacity, worldTransform),
      ...patternChildrenToDisplayItems(document, node, childClip, options, opacity, worldTransform, childDirtyAncestor),
    ];
  }

  if (node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance") {
    const childItems = childrenToDisplayItems(document, node.children, activeClip, options, opacity, worldTransform, childDirtyAncestor);
    return withContainerEffects(node, childItems, opacity);
  }

  if (node.type === "vectorNetwork") {
    const preview = options.vectorNetworkPreviews?.get(node.id);
    const items = preview
      ? previewNetworkToDisplayItems(node, preview)
      : networkToDisplayItems(node);
    return withNodeEffects(node, items.map(item => ({ ...item, clip: activeClip })), opacity, worldTransform);
  }

  if (node.type === "text") {
    return withNodeEffects(node, [{
      id: node.id,
      kind: "text",
      bounds: node.bounds,
      clip: activeClip,
      vertices: new Float32Array(0),
      fill: paintToDisplayPaint(node.style.fills.at(-1), node.bounds, "#f8fafc"),
      text: node.text,
      textFill: node.text.style.fill ?? node.style.fills.at(-1),
      textAutoLayoutFillWidth: flexChildLayout(node).fillWidth,
      scaleX: node.transform.scaleX,
      scaleY: node.transform.scaleY,
    }], opacity, worldTransform);
  }

  if (node.type === "shape") {
    const mesh = cachedPathMesh(node, node.path, () => pathDataToContours(node.path));
    const vertices = mesh.vertices;
    if (vertices.length === 0) {
      return [];
    }
    const items = fillDisplayItems(node, "triangles", node.bounds, vertices, activeClip, "#ffffff");
    const strokes = cachedStrokeDisplayItems(node, mesh).map(item => ({ ...item, clip: activeClip }));
    return withNodeEffects(node, [...items, ...strokes], opacity, worldTransform);
  }

  if (node.type === "boolean") {
    const displayNode = booleanPreviewNode(node, options.booleanSourcePreviews) ?? node;
    const mesh = cachedPathMesh(displayNode, displayNode.resolved.path, () => displayNode.resolved.contours);
    const vertices = mesh.vertices;
    if (vertices.length === 0) {
      return [];
    }
    const items = fillDisplayItems(displayNode, "triangles", displayNode.bounds, vertices, activeClip, "#ffffff");
    const strokes = cachedStrokeDisplayItems(displayNode, mesh).map(item => ({ ...item, clip: activeClip }));
    return withNodeEffects(displayNode, [...items, ...strokes], opacity, worldTransform);
  }

  if (node.type !== "primitive") {
    return [];
  }

  if (node.primitive.kind === "rectangle") {
    const items = fillDisplayItems(
      node,
      "roundedRect",
      node.bounds,
      packMesh(rectVertices(node.bounds)),
      activeClip,
      "#ffffff",
      normalizedCornerRadii(node.bounds, node.primitive.cornerRadii),
    );
    return withNodeEffects(node, [...items, ...strokeDisplayItems(node, primitiveToPolygon(node), true).map(item => ({ ...item, clip: activeClip }))], opacity, worldTransform);
  }

  if (node.primitive.kind === "oval") {
    const { arcStart = 0, arcSweep = 360, ratio = 0 } = node.primitive;
    const isFullCircle = arcSweep >= 360 && ratio === 0;

    if (isFullCircle) {
      const items = fillDisplayItems(node, "ellipse", node.bounds, packMesh(rectVertices(node.bounds)), activeClip, "#ffffff");
      return withNodeEffects(node, [...items, ...strokeDisplayItems(node, ellipsePoints(node.bounds, ellipseSegmentCount(node.bounds)), true).map(item => ({ ...item, clip: activeClip }))], opacity, worldTransform);
    }

    // Arc / donut / pie – use triangle mesh
    const triangleVerts = packMesh(arcOvalTriangles(node.bounds, arcStart, arcSweep, ratio));
    const strokeOutline = ovalOutlinePoints(node.bounds, arcStart, arcSweep, ratio, ellipseSegmentCount(node.bounds));
    const items = fillDisplayItems(node, "triangles", node.bounds, triangleVerts, activeClip, "#ffffff");
    return withNodeEffects(node, [...items, ...strokeDisplayItems(node, strokeOutline, ratio === 0 && arcSweep < 360).map(item => ({ ...item, clip: activeClip }))], opacity, worldTransform);
  }

  const vertices = packMesh(primitiveToTriangles(node));
  const items: DisplayItem[] = node.primitive.kind === "line"
    ? normalizeStrokeStack(node.style).filter(stroke => stroke.width > 0).map(stroke => ({
        id: node.id,
        kind: "triangles" as const,
        bounds: boundsFromPoints(primitiveToPolygon(node)),
        clip: activeClip,
        vertices,
        fill: paintToDisplayPaint(stroke.paint, node.bounds, "#ffffff"),
      }))
    : fillDisplayItems(node, "triangles", node.bounds, vertices, activeClip, "#ffffff");

  const strokes = node.primitive.kind === "line" ? [] : strokeDisplayItems(node, primitiveToPolygon(node), true).map(item => ({ ...item, clip: activeClip }));
  return withNodeEffects(node, [...items, ...strokes], opacity, worldTransform);
}

function childrenToDisplayItems(
  document: CurvaDocument,
  children: SceneNode[],
  activeClip: DisplayItem["clip"] | undefined,
  options: DisplayListOptions,
  inheritedOpacity: number,
  parentWorldTransform: Affine2D,
  dirtyAncestor = false,
): DisplayItem[] {
  const items: DisplayItem[] = [];
  let currentClip = activeClip;

  for (const child of children) {
    if (child.isMask) {
      currentClip = child.visible ? clipFromMaskNode(child, parentWorldTransform, activeClip) : activeClip;
      continue;
    }
    items.push(...nodeToDisplayItems(document, child, currentClip, options, inheritedOpacity, parentWorldTransform, dirtyAncestor));
  }

  return items;
}

function patternChildrenToDisplayItems(
  document: CurvaDocument,
  frame: FrameNode,
  activeClip: DisplayItem["clip"] | undefined,
  options: DisplayListOptions,
  inheritedOpacity: number,
  parentWorldTransform: Affine2D,
  dirtyAncestor = false,
): DisplayItem[] {
  if (!frameUsesPatternLayout(frame)) {
    return childrenToDisplayItems(document, frame.children, activeClip, options, inheritedOpacity, parentWorldTransform, dirtyAncestor);
  }

  const instances = patternInstancesForFrame(frame);
  const dirtyNodeIds = new Set(options.dirtyNodeIds ?? []);
  for (const child of frame.children) {
    dirtyNodeIds.add(child.id);
  }
  const instanceOptions: DisplayListOptions = { ...options, dirtyNodeIds };

  const items: DisplayItem[] = [];
  for (const instance of instances) {
    const instanceParent = multiplyAffine(parentWorldTransform, instance.transform);
    items.push(...childrenToDisplayItems(
      document,
      frame.children,
      activeClip,
      instanceOptions,
      inheritedOpacity,
      instanceParent,
      dirtyAncestor,
    ));
  }
  return items;
}

function clipFromMaskNode(
  node: SceneNode,
  parentWorldTransform: Affine2D,
  activeClip: DisplayItem["clip"] | undefined,
): DisplayItem["clip"] | undefined {
  const contours = sceneNodeToContours(node)
    .filter(contour => contour.length >= 3)
    .sort((left, right) => Math.abs(signedArea(right)) - Math.abs(signedArea(left)));
  const contour = contours[0];
  if (!contour) {
    return activeClip;
  }

  const polygon = contour.map(point => applyAffinePoint(parentWorldTransform, point));
  const maskClip = {
    bounds: boundsFromClipPoints(polygon),
    polygon,
  };
  return activeClip ? intersectClips(maskClip, activeClip) : maskClip;
}

function withNodeEffects(node: SceneNode, items: DisplayItem[], opacity: number, worldTransform: Affine2D): DisplayItem[] {
  const opacityItems = applyDisplayWorldTransform(items, worldTransform).map(item => displayItemWithOpacity(item, opacity));
  const effects = node.style.effects?.filter(effect => effect.visible);
  if (!effects || effects.length === 0 || opacityItems.length === 0) {
    return opacityItems;
  }

  const normalizedEffects = effects.map(effect => displayEffectWithOpacity(normalizeDisplayEffect(effect), opacity));
  const layerBlurEffects = normalizedEffects.filter(effect => effect.type === "layerBlur");
  return opacityItems.map((item, index) => {
    if (index === 0) {
      return { ...item, effects: normalizedEffects };
    }
    return layerBlurEffects.length > 0 ? { ...item, effects: layerBlurEffects } : item;
  });
}

function withContainerEffects(node: SceneNode, items: DisplayItem[], opacity: number): DisplayItem[] {
  const effects = node.style.effects?.filter(effect => effect.visible);
  if (!effects || effects.length === 0 || items.length === 0) {
    return items;
  }

  const normalizedEffects = effects.map(effect => displayEffectWithOpacity(normalizeDisplayEffect(effect), opacity));
  return items.map(item => ({
    ...item,
    effects: [...(item.effects ?? []), ...normalizedEffects],
  }));
}

function displayItemWithOpacity(item: DisplayItem, opacity: number): DisplayItem {
  if (opacity >= 1) {
    return item;
  }

  return {
    ...item,
    fill: displayPaintWithOpacity(item.fill, opacity),
    text: item.text ? textWithOpacity(item.text, opacity) : undefined,
    textFill: item.textFill ? paintWithOpacity(item.textFill, opacity) : undefined,
  };
}

function displayPaintWithOpacity(paint: DisplayPaint, opacity: number): DisplayPaint {
  if (paint.type === "solid") {
    return { ...paint, color: rgbaWithOpacity(paint.color, opacity) };
  }

  if (paint.type === "image") {
    return { ...paint, opacity: paint.opacity * opacity };
  }

  return {
    ...paint,
    stops: paint.stops.map(stop => ({ ...stop, color: rgbaWithOpacity(stop.color, opacity) })),
  };
}

function paintWithOpacity(paint: Paint, opacity: number): Paint {
  if (paint.type === "solid") {
    return { ...paint, color: colorWithOpacity(paint.color, opacity) };
  }

  if (paint.type === "image") {
    return { ...paint, opacity: paint.opacity * opacity };
  }

  return {
    ...paint,
    stops: paint.stops.map(stop => ({ ...stop, color: colorWithOpacity(stop.color, opacity) })),
  };
}

function textWithOpacity(text: RichTextModel, opacity: number): RichTextModel {
  return {
    ...text,
    style: typeStyleWithOpacity(text.style, opacity),
    styleOverrideTable: Object.fromEntries(
      Object.entries(text.styleOverrideTable).map(([id, style]) => [id, typeStyleWithOpacity(style, opacity)]),
    ),
  };
}

function typeStyleWithOpacity<T extends TypeStyle | Partial<TypeStyle>>(style: T, opacity: number): T {
  return style.fill ? { ...style, fill: paintWithOpacity(style.fill, opacity) } : style;
}

function displayEffectWithOpacity(effect: NonNullable<DisplayItem["effects"]>[number], opacity: number): NonNullable<DisplayItem["effects"]>[number] {
  if (effect.type === "dropShadow" || effect.type === "innerShadow") {
    return { ...effect, color: colorWithOpacity(effect.color, opacity) };
  }

  return effect;
}

function colorWithOpacity(color: OklchColor, opacity: number): OklchColor {
  const normalized = parseColor(color);
  return { ...normalized, alpha: normalized.alpha * opacity };
}

function rgbaWithOpacity(color: [number, number, number, number], opacity: number): [number, number, number, number] {
  return [color[0], color[1], color[2], color[3] * opacity];
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function normalizeDisplayEffect(effect: NonNullable<SceneNode["style"]["effects"]>[number]): NonNullable<DisplayItem["effects"]>[number] {
  if (effect.type === "dropShadow" || effect.type === "innerShadow") {
    return { ...effect, color: parseColor(effect.color) };
  }
  return { ...effect };
}
